/**
 * InstanceTableCore — the instance table both products' Manage Portfolio pages
 * share: every row added or uploaded, the server's per-cell remarks under the
 * cell, in-place editing, the Errors / Warnings bar, Delete and Delete Error
 * Records.
 *
 * Why a core: legacy InstancesListTable.vue is ONE component for CCA and EIA —
 * only the column list (EIA :420-545, CCA :339-418 / :595-627) and which cells
 * edit differ. So the behaviour lives here once, and each product's table
 * (components/eia/InstanceTable, components/cca/InstanceTable) is a thin
 * wrapper that passes its columns and edit rules. Built on the app's common
 * Table (paging, selection, skeleton). Kept from legacy:
 *  - each remark under its cell — red, or orange for "unsupported";
 *  - rows with remarks listed first;
 *  - select rows (per page) and Delete (n) behind a confirm dialog;
 *  - 10 / 25 / 50 / 100 rows per page;
 *  - EDIT A CELL IN PLACE: double-click it (or focus it and press Enter).
 *    Region / Instance Type / OS / Pricing Model edit as dropdowns, the
 *    numbers as number boxes; UUID is read-only. A dropdown pick saves at
 *    once; a number saves on Enter or when focus leaves; Esc or ✕ cancels.
 *    A field listed in `revertWhenEmptied` goes back to its old value when
 *    emptied (CCA Quantity / Hours, legacy handleQuantityChange :1205-1221).
 *    What happens to the change (required check, server re-check, revert on
 *    failure) is the page's `onEditCell`.
 *
 * Option lists are the page's too: `onEditStart` says which cell opened, so
 * the page can load regions, or the instance types of that row's region.
 * `onEditingChange(true|false)` says whether any edit is open or still being
 * checked by the server — the page's advice button waits for it (legacy
 * `isFieldUpdated` / editableField).
 *
 * Column config: `{ field, header, key?, numeric?, wide?, oci?, group?,
 * editable? }` — `oci` columns show only for OCI, `group` gives the two-row
 * header, `editable: false` locks a column even if its field could edit.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { ConfirmDialog } from '@components/common/ConfirmDialog';
import { Dropdown } from '@components/common/Dropdown';
import { ArrowRightIcon, ClearIcon } from '@components/common/icons';
import { NumberField } from '@components/common/NumberField';
import { Table } from '@components/common/Table';
import { Button, Modal } from '@components/ui';
import { useTouchEdit } from '@hooks/useTouchEdit';
import { cx } from '@lib/classNames';
import { VariantGate } from '@variant/VariantGate';

import { countRemarks, formatCell, isWarning, remarkFor, sortProblemsFirst } from '../instanceRows';
import { FIELD, OS_OPTIONS, PRICING_MODEL_OPTIONS, isOciFlex } from '../portfolioFormRules';

import styles from './InstanceTable.module.css';

const NO_FIELDS = Object.freeze([]);

/** OCPU / Memory mean nothing on a non-flex shape (legacy isOciNonFlexField). */
function isNotApplicable(row, field, provider) {
  return (
    (field === FIELD.OCPU || field === FIELD.MEMORY) &&
    !remarkFor(row, field) &&
    !isOciFlex(provider, row[FIELD.INSTANCE_TYPE])
  );
}

/** The dropdown value for a row's current value (OS is stored lower-case). */
function selectValueOf(field, value) {
  if (field !== FIELD.OS) return value ?? '';
  const match = OS_OPTIONS.find((o) => o.value.toLowerCase() === String(value ?? '').toLowerCase());
  return match ? match.value : (value ?? '');
}

/** What to store for a picked dropdown value (OS lower-case, as legacy sends it). */
function storedValueOf(field, value) {
  return field === FIELD.OS ? String(value).toLowerCase() : value;
}

/**
 * The open editor for one cell. Enter / Esc / leaving the cell are handled on
 * the wrapper, so NumberField and Dropdown keep their own key handling.
 */
function CellEditor({ field, kind, header, row, options, revertWhenEmpty, onCommit, onCancel }) {
  const original = kind === 'select' ? selectValueOf(field, row[field]) : (row[field] ?? '');
  const [draft, setDraft] = useState(original === null ? '' : String(original));
  // Once committed or cancelled, ignore the blur that follows.
  const done = useRef(false);

  const finish = (value) => {
    if (done.current) return;
    done.current = true;
    if (String(value) === String(original ?? '')) onCancel();
    // Emptied, and this cell keeps its old value instead (legacy CCA reverts).
    else if (revertWhenEmpty && String(value ?? '').trim() === '') onCancel();
    else onCommit(kind === 'select' ? storedValueOf(field, value) : value);
  };
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    onCancel();
  };

  const list =
    field === FIELD.OS
      ? OS_OPTIONS
      : field === FIELD.PRICING_MODEL
        ? PRICING_MODEL_OPTIONS
        : (options?.list ?? []);

  return (
    <div
      role="group"
      aria-label={`Edit ${header}`}
      className={styles.editor}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          cancel();
        } else if (e.key === 'Enter' && kind === 'number') {
          e.preventDefault();
          finish(draft);
        }
      }}
      onBlur={(e) => {
        // Focus moved out of the editor: a number saves (legacy saves on
        // blur), a dropdown left untouched just closes.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (kind === 'number') finish(draft);
        else cancel();
      }}
    >
      {kind === 'select' ? (
        <Dropdown
          className={styles.editorField}
          options={list}
          value={draft}
          loading={options?.loading}
          searchable={field === FIELD.REGION || field === FIELD.INSTANCE_TYPE}
          onChange={(next) => {
            setDraft(next);
            finish(next);
          }}
          hideDetails
          menuMinWidth={200}
          aria-label={header}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the user just asked to edit this cell
          autoFocus
        />
      ) : (
        <NumberField
          className={styles.editorField}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          min={0}
          integer={field === FIELD.MAX_IOPS}
          hideDetails
          aria-label={header}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the user just asked to edit this cell
          autoFocus
        />
      )}
      <button
        type="button"
        className={styles.cancelEdit}
        onMouseDown={(e) => e.preventDefault()} // keep focus, so blur does not save first
        onClick={cancel}
        aria-label={`Cancel editing ${header}`}
      >
        <ClearIcon />
      </button>
    </div>
  );
}

/**
 * One cell: the value (editable ones open the editor), and its remark underneath.
 * Opens on double-click or Enter / F2, and on a touch screen on double-tap or
 * long-press (useTouchEdit — a phone rarely sends a double-click).
 */
function Cell({ row, field, header, provider, editable, onStartEdit }) {
  const remark = remarkFor(row, field);
  const text = isNotApplicable(row, field, provider) ? '—' : formatCell(row[field], field);
  const touch = useTouchEdit(onStartEdit);

  return (
    <div className={styles.cell}>
      {editable ? (
        <button
          type="button"
          className={cx(styles.value, styles.editable, remark && styles.valueFlagged)}
          title={text}
          aria-label={`${header}: ${text}. Double-click or press Enter to edit`}
          {...touch}
          onDoubleClick={onStartEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'F2') {
              e.preventDefault();
              onStartEdit();
            }
          }}
        >
          {text}
        </button>
      ) : (
        <span className={cx(styles.value, remark && styles.valueFlagged)} title={text}>
          {text}
        </span>
      )}
      {remark ? (
        <span
          className={cx(styles.remark, isWarning(remark) && styles.remarkWarning)}
          title={remark}
        >
          {remark}
        </span>
      ) : null}
    </div>
  );
}

export function InstanceTableCore({
  rows,
  provider,
  /** The product's columns (see the config note above). */
  columns: columnConfig,
  /** field -> 'select' | 'number': the cells that can be edited, and how. */
  editableFields,
  /** Number cells that go back to their old value when emptied. */
  revertWhenEmptied = NO_FIELDS,
  /**
   * Last step of the "Fix the errors → Save portfolio → Obtain …" hint. Without
   * it the product decides ("Instance advice" on EIA, "Cost advice" on CCA).
   */
  adviceLabel,
  loading = false,
  onDelete,
  /** Delete Error Records; async, the dialog waits for it. No button without it. */
  onDeleteErrors,
  /** Busy elsewhere on the page (saving, uploading…): Delete Error Records waits. */
  actionsDisabled = false,
  onEditCell,
  onEditStart,
  onEditingChange,
  regionOptions,
  instanceTypeOptions,
  /** What an empty table says (a new portfolio vs an opened one with no data). */
  emptyMessage = 'No instances added yet.',
  /** Address of the provider's region list page; the link is hidden without it. */
  regionsHref,
}) {
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** "Confirm Delete Error Records?": closed (null), open, or running ('busy'). */
  const [errorsDialog, setErrorsDialog] = useState(null);
  const { errors, warnings } = countRemarks(rows);

  const confirmDeleteErrors = async () => {
    setErrorsDialog('busy');
    try {
      await onDeleteErrors?.();
    } finally {
      setErrorsDialog(null);
    }
  };
  /** The open editor: { rowId, field } or null. */
  const [editing, setEditing] = useState(null);
  /** Rows waiting for the server after an edit. */
  const [savingIds, setSavingIds] = useState(() => new Set());

  const isOci = String(provider ?? '').toUpperCase() === 'OCI';
  const canEdit = typeof onEditCell === 'function';

  // Tell the page when editing begins and ends (open editor, or a row the
  // server is still checking). False again on unmount, so it never sticks.
  const editingNow = editing !== null || savingIds.size > 0;
  const reportEditing = useRef(onEditingChange);
  reportEditing.current = onEditingChange;
  useEffect(() => {
    reportEditing.current?.(editingNow);
  }, [editingNow]);
  useEffect(() => () => reportEditing.current?.(false), []);

  const startEdit = (row, field) => {
    if (savingIds.has(row._rowId)) return;
    setEditing({ rowId: row._rowId, field });
    onEditStart?.({ row, field });
  };

  const commitEdit = async (row, field, value) => {
    setEditing(null);
    setSavingIds((ids) => new Set(ids).add(row._rowId));
    try {
      await onEditCell(row, field, value);
    } finally {
      setSavingIds((ids) => {
        const next = new Set(ids);
        next.delete(row._rowId);
        return next;
      });
    }
  };

  const columns = useMemo(
    () =>
      columnConfig
        .filter((c) => !c.oci || isOci)
        .map((c) => ({
          key: c.key ?? c.field,
          header: c.header,
          group: c.group,
          numeric: c.numeric,
          className: c.wide ? styles.wide : undefined,
          render: (row) => {
            const isEditing = editing?.rowId === row._rowId && editing.field === c.field;
            if (isEditing) {
              const options =
                c.field === FIELD.REGION
                  ? regionOptions
                  : c.field === FIELD.INSTANCE_TYPE
                    ? instanceTypeOptions
                    : undefined;
              return (
                <CellEditor
                  field={c.field}
                  kind={editableFields[c.field]}
                  header={c.header}
                  row={row}
                  options={options}
                  revertWhenEmpty={revertWhenEmptied.includes(c.field)}
                  onCommit={(value) => commitEdit(row, c.field, value)}
                  onCancel={() => setEditing(null)}
                />
              );
            }
            const editable =
              canEdit &&
              c.editable !== false &&
              Boolean(editableFields[c.field]) &&
              !savingIds.has(row._rowId) &&
              !isNotApplicable(row, c.field, provider);
            return (
              <Cell
                row={row}
                field={c.field}
                header={c.header}
                provider={provider}
                editable={editable}
                onStartEdit={() => startEdit(row, c.field)}
              />
            );
          },
        })),
    // commitEdit / startEdit close over the props and state listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      columnConfig,
      editableFields,
      revertWhenEmptied,
      isOci,
      provider,
      editing,
      savingIds,
      canEdit,
      regionOptions,
      instanceTypeOptions,
    ],
  );

  const sorted = useMemo(() => sortProblemsFirst(rows), [rows]);

  // A selection can outlive its rows (deleted elsewhere): count only live ones.
  const liveIds = new Set(rows.map((r) => r._rowId));
  const selectedLive = selected.filter((id) => liveIds.has(id));

  const confirmDelete = () => {
    onDelete?.(selectedLive);
    setSelected([]);
    setConfirmOpen(false);
  };

  // Cancel in the dialog drops the selection too, as legacy's cancelDelete
  // does; closing it any other way (Esc, ✕) only closes it.
  const cancelDelete = () => {
    setSelected([]);
    setConfirmOpen(false);
  };

  return (
    <section className={styles.instanceTable} aria-labelledby="instance-table-title">
      <header className={styles.header}>
        {/* The design's small teal section label and its hint; the row count
            lives in the table footer ("Showing 1–n of n"). */}
        <div className={styles.heading}>
          <h2 id="instance-table-title" className={styles.title}>
            Instances
          </h2>
          {canEdit ? (
            <p className={styles.hint}>Double-click any cell to update its value.</p>
          ) : null}
        </div>
      </header>

      {/* Legacy's BulkDelete bar (BulkDelete.vue): the counts, and — while any
          ERROR is left — what to do next and a way to drop every error row.
          Warnings ("unsupported …") are counted but never block anything. */}
      {rows.length > 0 && (errors > 0 || warnings > 0) ? (
        <div className={styles.statusBar}>
          <p className={styles.counts} role="status">
            {errors > 0 ? <span className={styles.errorCount}>Errors: {errors}</span> : null}
            {warnings > 0 ? (
              <span className={styles.warningCount}>Warnings: {warnings}</span>
            ) : null}
          </p>
          {errors > 0 ? (
            <>
              <p className={styles.flow}>
                Fix the errors
                <ArrowRightIcon aria-hidden="true" />
                Save portfolio
                <ArrowRightIcon aria-hidden="true" />
                Obtain{' '}
                {adviceLabel ?? (
                  <VariantGate only="eia" fallback="Cost advice">
                    Instance advice
                  </VariantGate>
                )}
              </p>
              {onDeleteErrors ? (
                <Button
                  variant="danger"
                  className={cx(styles.deleteButton, styles.deleteErrors)}
                  disabled={actionsDisabled || loading || errorsDialog !== null}
                  onClick={() => setErrorsDialog('open')}
                >
                  Delete Error Records
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      <Table
        caption="Portfolio instances"
        columns={columns}
        rows={sorted}
        rowKey={(row) => row._rowId}
        loading={loading}
        selectable
        selectedKeys={selectedLive}
        onSelectionChange={setSelected}
        getRowClassName={(row) => (savingIds.has(row._rowId) ? styles.savingRow : undefined)}
        emptyMessage={emptyMessage}
      />

      {/* Under the table, as legacy (InstancesListTable.vue:228-254): Delete on
          the left, the provider's region list on the right (new tab). */}
      <div className={styles.footerBar}>
        <Button
          variant="danger"
          className={styles.deleteButton}
          disabled={selectedLive.length === 0 || loading}
          onClick={() => setConfirmOpen(true)}
        >
          Delete{selectedLive.length > 0 ? ` (${selectedLive.length})` : ''}
        </Button>
        {regionsHref ? (
          <a
            className={styles.regionsLink}
            href={regionsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            List of {provider} Regions
            <ArrowRightIcon />
          </a>
        ) : null}
      </div>

      {errorsDialog ? (
        // Legacy's wording and buttons (BulkDelete.vue :38-57).
        <ConfirmDialog
          title="Confirm Delete Error Records?"
          message="Are you sure you want to delete?"
          cancelLabel="Cancel"
          confirmLabel="Delete"
          danger
          busy={errorsDialog === 'busy'}
          onConfirm={confirmDeleteErrors}
          onCancel={() => setErrorsDialog(null)}
        />
      ) : null}

      {confirmOpen ? (
        <Modal
          open
          onClose={() => setConfirmOpen(false)}
          title="Confirm Delete"
          footer={
            <div className={styles.dialogActions}>
              <Button variant="secondary-solid" onClick={cancelDelete}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          }
        >
          <p className={styles.dialogText}>
            Are you sure you want to delete {selectedLive.length} instance
            {selectedLive.length === 1 ? '' : 's'}?
          </p>
        </Modal>
      ) : null}
    </section>
  );
}
